/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import httpClient, { extractData, tokenStorage } from './http-client';
import { ApiResponse, User, UserProfile, LoginRequest, RegisterRequest, TokenResponse } from '../types';

class AuthService {
  /**
   * Login with email and password
   */
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    const response = await httpClient.post<ApiResponse<TokenResponse>>(
      '/auth/login',
      credentials
    );
    return extractData(response);
  }

  /**
   * Register a new user
   */
  async register(data: RegisterRequest): Promise<TokenResponse> {
    const response = await httpClient.post<ApiResponse<TokenResponse>>(
      '/auth/register',
      data
    );
    return extractData(response);
  }

  /**
   * Logout current user
   */
  async logout(): Promise<void> {
    try {
      await httpClient.post('/auth/logout');
    } finally {
      tokenStorage.clearTokens();
    }
  }

  /**
   * Get current user profile (from auth, minimal fields)
   */
  async getCurrentUser(): Promise<User> {
    const response = await httpClient.get<ApiResponse<User>>('/auth/me', {
      params: { _ts: Date.now() },
    });
    return extractData(response);
  }

  /**
   * Get full profile including team (GET /users/me)
   */
  async getProfile(): Promise<UserProfile> {
    const response = await httpClient.get<ApiResponse<UserProfile>>('/users/me');
    return extractData(response);
  }

  /**
   * Change own password (PUT /users/me/password)
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await httpClient.put('/users/me/password', {
      currentPassword,
      newPassword,
    });
  }

  /**
   * Upload avatar image (multipart/form-data, field name: avatar)
   */
  async uploadAvatar(file: File): Promise<{ avatarPath: string }> {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await httpClient.post<ApiResponse<{ avatarPath: string }>>('/users/me/avatar', formData, {
      transformRequest: [(data, headers) => {
        if (data instanceof FormData && headers) {
          delete (headers as Record<string, unknown>)['Content-Type'];
        }
        return data;
      }],
    });
    return extractData(response);
  }

  /**
   * Refresh access token (body variant; backend also accepts refresh token in body).
   */
  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    const response = await httpClient.post<ApiResponse<TokenResponse>>(
      '/auth/refresh',
      { refreshToken }
    );
    return extractData(response);
  }

  /**
   * Restore session on app load using httpOnly refresh cookie.
   * Call with no body; backend reads refresh token from cookie.
   * Returns true if a new access token was stored, false otherwise.
   */
  async restoreSession(): Promise<boolean> {
    try {
      const response = await httpClient.post<ApiResponse<TokenResponse>>('/auth/refresh', {});
      const data = extractData(response);
      if (data?.accessToken) {
        tokenStorage.setTokens(data.accessToken, '');
        return true;
      }
    } catch {
      // No cookie or invalid refresh token
    }
    return false;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return tokenStorage.hasTokens();
  }

  /**
   * Store authentication tokens
   */
  setTokens(accessToken: string, refreshToken: string): void {
    tokenStorage.setTokens(accessToken, refreshToken);
  }

  /**
   * Clear authentication tokens
   */
  clearTokens(): void {
    tokenStorage.clearTokens();
  }

  /**
   * Exit impersonation: restore admin tokens from backup and redirect to admin dashboard.
   * Call when "Back to admin" is clicked (only when tokenStorage.hasImpersonationBackup()).
   */
  exitImpersonation(): void {
    const backup = tokenStorage.getImpersonationBackup();
    if (!backup) return;
    tokenStorage.setTokens(backup.accessToken, backup.refreshToken);
    tokenStorage.clearImpersonationBackup();
    window.location.href = '/dashboard/admin';
  }

  /**
   * Analyst impersonate review requester: get tokens for the user who requested the review,
   * save current (analyst) tokens, set new tokens, and reload.
   */
  async analystImpersonateReviewUser(reviewId: string): Promise<void> {
    const response = await httpClient.post<ApiResponse<TokenResponse>>(
      `/analyst-reviews/${reviewId}/impersonate`
    );
    const raw = response.data as any;
    const tokens = raw?.success && raw?.data ? raw.data : raw?.accessToken ? raw : null;
    if (!tokens?.accessToken) throw new Error('API request failed');
    const currentAccess = tokenStorage.getAccessToken();
    if (currentAccess) {
      tokenStorage.setAnalystImpersonationBackup(currentAccess, tokenStorage.getRefreshToken() ?? '');
    }
    tokenStorage.setTokens(tokens.accessToken, tokens.refreshToken ?? '');
    window.location.reload();
  }

  /**
   * Exit analyst impersonation: restore analyst tokens and go to dashboard.
   */
  exitAnalystImpersonation(): void {
    const backup = tokenStorage.getAnalystImpersonationBackup();
    if (!backup) return;
    tokenStorage.setTokens(backup.accessToken, backup.refreshToken);
    tokenStorage.clearAnalystImpersonationBackup();
    window.location.href = '/dashboard/analyst';
  }
}

export const authService = new AuthService();
export default authService;
